import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Send, MessageCircle } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { LOGO } from "@/lib/ellaApi";
import { useStore } from "@/lib/store";
import { toast } from "sonner";

export default function MessageDetailPage() {
  const { branchId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { branches } = useStore();
  // Prefer the name passed via navigation; fall back to the store (deep-link/reload).
  const branchName = loc.state?.name || loc.state?.branchName
    || branches.find((b) => b.ID === branchId)?.Name || "Cabang";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Prefilled contextual message passed from a product/transaction page
  const [text, setText] = useState(() => loc.state?.prefill || "");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await ella.getChatDetail(branchId);
      if (res.status) setMessages(res.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 15000);
    return () => clearInterval(t);
  }, [branchId]); // eslint-disable-line

  // Scroll the messages container (not the window) to the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Put the cursor in the composer when we arrive with a prefilled message.
  useEffect(() => {
    if (loc.state?.prefill && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [loc.state?.prefill]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = text.replace(/'/g, "").trim();
    if (!msg) return;
    setSending(true);
    // Optimistic append so the user sees their bubble immediately
    const tempId = `tmp-${Date.now()}`;
    setMessages((prev) => [...prev, { ID: tempId, Message: msg, IsReply: 0, IsReadByBranch: 0, IsReadByCustomer: 1, _pending: true }]);
    setText("");
    try {
      const res = await ella.sendChatMessage(branchId, msg);
      if (res.status) {
        fetchMessages();
      } else {
        toast.error(res.message || "Gagal mengirim pesan");
        setMessages((prev) => prev.filter((m) => m.ID !== tempId));
        setText(msg);
      }
    } catch {
      toast.error("Gagal mengirim pesan");
      setMessages((prev) => prev.filter((m) => m.ID !== tempId));
      setText(msg);
    } finally { setSending(false); }
  };

  return (
    // Fixed-height column below the app header — the messages area scrolls
    // internally so nothing hides under the header (no window scroll). The
    // composer is pinned to the true viewport bottom, immune to vh/dvh quirks.
    <div className="flex flex-col overflow-hidden" style={{ height: "calc(100dvh - var(--app-header-height, 60px))" }}>
      {/* Chat header */}
      <div className="px-4 py-3 flex items-center gap-3 bg-white border-b border-gray-100 shrink-0">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
          <img src={LOGO} alt="" className="w-6 h-6 object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm leading-tight" data-testid="chat-branch-name">Admin {branchName}</p>
          <p className="text-[10px] text-emerald-600">● Aktif</p>
        </div>
      </div>

      {/* Messages — the only scrollable region (pb clears the fixed composer) */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-20 space-y-2 bg-gray-50" data-testid="chat-messages-container">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
                <div className="w-2/3 h-10 skeleton rounded-2xl" />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="py-12 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Mulai percakapan dengan admin {branchName}</p>
          </div>
        ) : (
          messages.map((m) => {
            const isMine = Number(m.IsReply) === 0;
            return (
              <div key={m.ID} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.ID}`}>
                <div
                  className={`max-w-[78%] px-3.5 py-2 text-sm rounded-2xl whitespace-pre-wrap break-words ${
                    isMine
                      ? `bg-[#D32F2F] text-white rounded-br-sm ${m._pending ? "opacity-70" : ""}`
                      : "bg-white text-gray-900 border border-gray-100 rounded-bl-sm"
                  }`}
                >
                  {m.Message}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer — pinned to the true viewport bottom */}
      <form onSubmit={send} className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-3 py-2.5 flex items-center gap-2 z-40">
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Tulis pesan..."
          className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm outline-none focus:bg-gray-50 focus:ring-2 focus:ring-red-200"
          data-testid="chat-message-input"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="w-10 h-10 rounded-full bg-[#D32F2F] text-white flex items-center justify-center disabled:bg-gray-200 disabled:text-gray-400 active:scale-95 shrink-0"
          data-testid="chat-send-button"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
